// src/api/features/tour-types/api.ts

import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  TourType,
  TourTypeListQuery,
  PaginatedTourTypeItems,
  CreateTourTypePayload,
  UpdateTourTypePayload,
} from "./types";

/* ======================================================
 * Helpers
 * ====================================================== */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

/* ======================================================
 * Base URL
 * ------------------------------------------------------
 * Adjust to your PATHS config. Expected backend route:
 *   /api/v1/tour-types
 * so this should usually be PATHS.tourTypes.base
 * ====================================================== */

const TOUR_TYPES_BASE = PATHS.tourTypes.base;

/* ======================================================
 * LIST – GET /tour-types
 * Returns unwrapped pagination for FE: PaginatedTourTypeItems
 * ====================================================== */

export async function listTourTypes(
  query: TourTypeListQuery = {}
): Promise<PaginatedTourTypeItems> {
  const safePage = Math.max(1, Number(query.page ?? 1));

  // FE uses `limit`, backend expects `pageSize`
  const rawPageSize = Number(query.limit ?? 10);
  const safePageSize = Math.max(
    1,
    Math.min(100, Number.isFinite(rawPageSize) ? rawPageSize : 10)
  );

  const url = `${TOUR_TYPES_BASE}${qs({
    page: safePage,
    pageSize: safePageSize,
    q: query.q,
  })}`;

  const res = await request<any>({
    method: "GET",
    url,
  });

  // 1) Backend might already return the paginated object directly:
  //    { items, page, pageSize, total }
  if (
    res &&
    typeof res === "object" &&
    "items" in res &&
    "page" in res &&
    "pageSize" in res &&
    "total" in res
  ) {
    const d = res as PaginatedTourTypeItems;

    return {
      items: d.items ?? [],
      page: Number(d.page ?? safePage),
      pageSize: Number(d.pageSize ?? safePageSize),
      total: Number(d.total ?? d.items?.length ?? 0),
    };
  }

  // 2) Backend returns an envelope:
  //    { ok, data: { items, page, pageSize, total } }
  // or { ok, data: TourType[], meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const env = res as ApiEnvelope<unknown> & { meta?: any };
    const data = env.data;

    // 2a) data is the paginated object
    if (data && typeof data === "object" && "items" in (data as any)) {
      const d = data as PaginatedTourTypeItems;

      return {
        items: d.items ?? [],
        page: Number(d.page ?? safePage),
        pageSize: Number(d.pageSize ?? safePageSize),
        total: Number(d.total ?? d.items?.length ?? 0),
      };
    }

    // 2b) data is plain array + meta
    if (Array.isArray(data)) {
      const items = data as TourType[];
      const meta = env.meta ?? {};

      return {
        items,
        page: Number(meta.page ?? safePage),
        pageSize: Number(meta.pageSize ?? meta.limit ?? safePageSize),
        total: Number(meta.total ?? items.length ?? 0),
      };
    }
  }

  // 3) Fallback – assume it's already PaginatedTourTypeItems
  return res as PaginatedTourTypeItems;
}


/* ======================================================
 * DETAIL – GET /tour-types/:id
 * Returns: TourType (backend may send extra audit fields)
 * ====================================================== */

export async function getTourType(id: number | string): Promise<TourType> {
  const res = await request<TourType | ApiEnvelope<TourType>>({
    method: "GET",
    url: `${TOUR_TYPES_BASE}/${id}`,
  });

  return unwrap<TourType>(res);
}

/* ======================================================
 * CREATE – POST /tour-types
 * Body: CreateTourTypePayload
 * Returns: TourType
 * ====================================================== */

export async function createTourType(
  payload: CreateTourTypePayload
): Promise<TourType> {
  const dto: any = { ...payload };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createTourType] payload →", dto);
  }

  const res = await request<TourType | ApiEnvelope<TourType>>({
    method: "POST",
    url: TOUR_TYPES_BASE,
    data: dto,
  });

  return unwrap<TourType>(res);
}

/* ======================================================
 * UPDATE – PUT /tour-types/:id
 * Body: UpdateTourTypePayload
 * Returns: TourType
 * ====================================================== */

export async function updateTourType(
  id: number | string,
  payload: UpdateTourTypePayload
): Promise<TourType> {
  const dto: any = { ...payload };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateTourType] id=", id, "payload →", dto);
  }

  const res = await request<TourType | ApiEnvelope<TourType>>({
    method: "PUT",
    url: `${TOUR_TYPES_BASE}/${id}`,
    data: dto,
  });

  return unwrap<TourType>(res);
}

/* ======================================================
 * DELETE – DELETE /tour-types/:id
 * Returns: simple message or nothing
 * ====================================================== */

export async function deleteTourType(
  id: number | string
): Promise<{ message: string } | void> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${TOUR_TYPES_BASE}/${id}`,
  });

  // If API returns plain 204 No Content, res may be undefined
  if (!res) return;

  return unwrap<{ message: string }>(res);
}
